import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../BrandLogo';
import StudentNotificationBell from './StudentNotificationBell';
import '../../styles/staff-dashboard.css';

const NAV_ITEMS = [
  { id: 'top', label: 'Dashboard', href: '/student/dashboard' },
  { id: 'sd-my-details', label: 'My details', section: 'sd-my-details' },
  { id: 'sd-courses', label: 'Courses', section: 'sd-courses' },
  { id: 'sd-assignments', label: 'Assignments', section: 'sd-assignments' },
  { id: 'sd-performance', label: 'Performance', section: 'sd-performance' },
  { id: 'sd-projects', label: 'Projects', section: 'sd-projects' },
  { id: 'sd-placement', label: 'Placement', section: 'sd-placement' },
  { id: 'sd-profile', label: 'Profile', section: 'sd-profile' },
  { id: 'notifications', label: 'Notifications', href: '/student/notifications' },
];

function scrollToSection(sectionId) {
  if (sectionId === 'top') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const el = document.getElementById(sectionId);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Responsive shell: collapsible sidebar + top navbar for the learner dashboard.
 */
const StudentDashboardShell = ({
  displayName,
  email,
  avatarLetter,
  onLogout,
  children
}) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const go = useCallback((item) => {
    if (item.href) {
      if (window.location.pathname === item.href) {
        scrollToSection(item.id === 'top' ? 'top' : item.section || 'top');
      } else {
        navigate(item.href);
      }
      setSidebarOpen(false);
      return;
    }
    if (item.section) {
      if (window.location.pathname !== '/student/dashboard') {
        navigate('/student/dashboard');
        setTimeout(() => scrollToSection(item.section), 120);
      } else {
        scrollToSection(item.section);
      }
    }
    setSidebarOpen(false);
  }, [navigate]);

  return (
    <div className="staff-home student-dashboard-shell min-h-screen flex">
      {/* Mobile overlay */}
      {sidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      {/* Sidebar */}
      <aside
        className={`
          student-dash-sidebar fixed z-40 inset-y-0 left-0 w-64 transform border-r transition-transform duration-200 ease-out
          lg:translate-x-0 lg:static lg:z-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex h-full flex-col">
          <div className="student-dash-sidebar-brand p-5 border-b">
            <BrandLogo size="sm" showWordmark />
            <p className="student-dash-eyebrow mt-3">Learner portal</p>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item)}
                className="student-dash-nav-link w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top navbar */}
        <header className="student-dash-topbar sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="student-dash-menu-btn lg:hidden p-2 rounded-lg"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <span className="text-lg" aria-hidden>
                ☰
              </span>
            </button>
            <div className="min-w-0">
              <p className="student-dash-user-name text-sm font-semibold truncate">
                {displayName || 'Student'}
              </p>
              <p className="student-dash-user-email text-xs truncate">{email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <StudentNotificationBell enabled />
            <div className="student-dash-avatar flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white shrink-0" title={displayName}>
              {avatarLetter}
            </div>
            <button type="button" onClick={onLogout} className="staff-logout">
              Logout
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">{children}</main>
      </div>
    </div>
  );
};

export default StudentDashboardShell;
